
import { useEffect, useRef, useCallback } from 'react'

interface UseInactivityTimeoutOptions {
  timeout: number // timeout in milliseconds
  onTimeout: () => void
  onWarning?: (secondsRemaining: number) => void // called when warning period starts
  warningTime?: number // warning time in milliseconds (default: 60000 = 1 minute)
  isActive: boolean // whether the timeout should be active
}

export function useInactivityTimeout({ timeout, onTimeout, onWarning, warningTime = 60000, isActive }: UseInactivityTimeoutOptions) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastActivityRef = useRef<number>(Date.now())
  const warningShownRef = useRef<boolean>(false) // Track if warning has been shown for current timeout period

  // Clear all timeouts
  const clearInactivityTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current)
      warningTimeoutRef.current = null
    }
  }, [])

  // Reset the timeout
  const resetTimeout = useCallback(() => {
    if (!isActive) {
      console.log('[InactivityTimeout] Timeout is not active, skipping reset')
      return
    }

    lastActivityRef.current = Date.now()
    console.log('[InactivityTimeout] Resetting timeout, next expiry in', timeout / 1000, 'seconds')

    // Clear all existing timeouts
    clearInactivityTimeout()

    // Reset warning shown flag when timeout is reset due to activity
    warningShownRef.current = false

    // Set warning timeout if warning callback is provided
    if (onWarning && warningTime < timeout) {
      const warningDelay = timeout - warningTime
      console.log('[InactivityTimeout] Setting warning timeout for', warningDelay / 1000, 'seconds')

      warningTimeoutRef.current = setTimeout(() => {
        // Only show warning if it hasn't been shown yet for this timeout period
        if (!warningShownRef.current) {
          console.log('[InactivityTimeout] Warning period started, showing countdown')
          const remainingSeconds = Math.ceil(warningTime / 1000)

          // Mark warning as shown for this timeout period
          warningShownRef.current = true

          // Call warning callback only once with the initial countdown value
          onWarning(remainingSeconds)
        } else {
          console.log('[InactivityTimeout] Warning already shown for this timeout period, skipping')
        }
      }, warningDelay)
    }

    // Set main timeout
    timeoutRef.current = setTimeout(() => {
      console.log('[InactivityTimeout] Session expired due to inactivity after', timeout / 1000, 'seconds')
      console.log('[InactivityTimeout] Last activity was at:', new Date(lastActivityRef.current).toISOString())

      onTimeout()
    }, timeout)
  }, [timeout, onTimeout, onWarning, warningTime, isActive, clearInactivityTimeout])

  // Activity event handler
  const handleActivity = useCallback(() => {
    if (isActive) {
      resetTimeout()
    }
  }, [resetTimeout, isActive])

  useEffect(() => {
    if (!isActive) {
      console.log('[InactivityTimeout] Timeout is not active, clearing existing timeout')
      clearInactivityTimeout()
      return
    }

    console.log('[InactivityTimeout] Setting up inactivity timeout with', timeout / 1000, 'second timeout')

    // Events to track for user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ]

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true })
    })

    console.log('[InactivityTimeout] Added event listeners for:', events.join(', '))

    // Start the timeout
    resetTimeout()

    // Cleanup
    return () => {
      console.log('[InactivityTimeout] Cleaning up event listeners and timeout')
      events.forEach(event => {
        document.removeEventListener(event, handleActivity)
      })
      clearInactivityTimeout()
    }
  }, [handleActivity, resetTimeout, clearInactivityTimeout, isActive, timeout, onWarning, warningTime])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return {
    resetTimeout,
    clearInactivityTimeout,
    getLastActivity: () => lastActivityRef.current
  }
}
